import type { FolderScope } from "../../shared/folderScopes";
import type { ApiFolder } from "../../shared/interfaces";
import type { ProjectTransport } from "./transport";
import {
    optionalField,
    requireArray,
    requireFiniteNumber,
    requireNonNegativeInteger,
    requireNullableString,
    requireRecord,
    requireString,
    requireWireString,
    validateResponseListRows,
} from "./validation";

const FOLDERS_ENDPOINT = "/api/ai/folders";

export interface FolderCreateInput {
    name: string;
    parent_id?: string;
    /** The top-level folder a folder without a parent goes in (Additional docs when omitted);
     *  a subfolder is in its parent's. */
    scope?: FolderScope;
}

export interface FolderUpdateInput {
    name?: string;
    parent_id?: string | null;
    sort_order?: number;
}

export interface FoldersListResponse {
    folders: ApiFolder[];
    /** The documents in the listed top-level folder(s), at their top level included. */
    total_document_count: number;
    /** `scope=all` only: each top-level folder's total, every scope present. */
    total_document_counts?: Partial<Record<FolderScope, number>>;
}

/** What `GET /folders` lists: one top-level folder's tree (Additional docs' when omitted, the
 *  request the shipped iOS client makes), or `all` of them with each one's total. */
export type FolderListScope = FolderScope | "all";

export interface DeleteFolderResponse {
    deleted: string;
    deleted_document_count: number;
}

function validateFolder(value: unknown, endpoint: string, field: string): ApiFolder {
    const folder = requireRecord(value, endpoint, field);
    requireString(folder.id, endpoint, `${field}.id`);
    requireNullableString(folder.parent_id, endpoint, `${field}.parent_id`);
    requireWireString(folder.name, endpoint, `${field}.name`);
    requireWireString(folder.path, endpoint, `${field}.path`);
    requireFiniteNumber(folder.sort_order, endpoint, `${field}.sort_order`);
    requireNonNegativeInteger(folder.document_count, endpoint, `${field}.document_count`);
    const scope = optionalField(folder, "scope");
    if (scope !== undefined) requireString(scope, endpoint, `${field}.scope`);
    return folder as unknown as ApiFolder;
}

export interface ProjectFoldersApi {
    list(signal?: AbortSignal, scope?: FolderListScope): Promise<FoldersListResponse>;
    create(input: FolderCreateInput, signal?: AbortSignal): Promise<ApiFolder>;
    update(id: string, input: FolderUpdateInput, signal?: AbortSignal): Promise<ApiFolder>;
    remove(id: string, signal?: AbortSignal): Promise<DeleteFolderResponse>;
}

export function createFoldersApi(transport: ProjectTransport): ProjectFoldersApi {
    return {
        async list(signal, scope) {
            const response = await transport.request<unknown>({
                method: "GET",
                path: FOLDERS_ENDPOINT,
                ...(scope ? { query: { scope } } : {}),
                signal,
            });
            const payload = requireRecord(response, FOLDERS_ENDPOINT, "response");
            const rows = requireArray(payload.folders, FOLDERS_ENDPOINT, "folders");
            const folders = validateResponseListRows(
                rows,
                FOLDERS_ENDPOINT,
                "folder",
                (value, field) => validateFolder(value, FOLDERS_ENDPOINT, field),
                undefined,
                "folders",
            );
            requireNonNegativeInteger(
                payload.total_document_count,
                FOLDERS_ENDPOINT,
                "total_document_count",
            );
            const totals = optionalField(payload, "total_document_counts");
            if (totals !== undefined) {
                const counts = requireRecord(totals, FOLDERS_ENDPOINT, "total_document_counts");
                for (const [key, count] of Object.entries(counts))
                    requireNonNegativeInteger(
                        count,
                        FOLDERS_ENDPOINT,
                        `total_document_counts.${key}`,
                    );
            }
            return folders === rows
                ? (payload as unknown as FoldersListResponse)
                : ({ ...payload, folders } as unknown as FoldersListResponse);
        },

        async create(input, signal) {
            const response = await transport.request<unknown>({
                method: "POST",
                path: FOLDERS_ENDPOINT,
                body: input,
                signal,
            });
            return validateFolder(response, FOLDERS_ENDPOINT, "response");
        },

        async update(id, input, signal) {
            const path = `${FOLDERS_ENDPOINT}/${id}`;
            const response = await transport.request<unknown>({
                method: "PATCH",
                path,
                body: input,
                signal,
            });
            return validateFolder(response, path, "response");
        },

        async remove(id, signal) {
            const path = `${FOLDERS_ENDPOINT}/${id}`;
            const response = await transport.request<unknown>({
                method: "DELETE",
                path,
                signal,
            });
            const payload = requireRecord(response, path, "response");
            return {
                deleted: requireString(payload.deleted, path, "deleted"),
                deleted_document_count: requireNonNegativeInteger(
                    payload.deleted_document_count,
                    path,
                    "deleted_document_count",
                ),
            };
        },
    };
}
